import {Button} from '@wandb/weave/components';
import {maybePluralizeWord} from '@wandb/weave/core/util/string';
import React, {useCallback, useMemo, useState} from 'react';

import {Run} from '../../../util/runTypes';
import {InstrumentedCenteredWaveLoader} from '../../utility/InstrumentedLoader';
import {InlineCombobox} from './InlineCombobox/InlineCombobox';
import {Body, Container, Footer, Header, Progress} from './MoveRunDrawerPieces';
import {useRunGroups} from './useRunGroups';
import {useRunMover} from './useRunMover';

type MoveRunDrawerContents = {
  entityName: string;
  isInReport: boolean | undefined;
  onClose: () => void;
  onMovedRuns: () => void;
  projectName: string;
  runs: Run[];
};

export function MoveRunDrawerContents({
  entityName,
  isInReport,
  onClose,
  onMovedRuns,
  projectName,
  runs,
}: MoveRunDrawerContents) {
  const {
    groups,
    loading,
    refetch: refetchRunGroups,
  } = useRunGroups(entityName, projectName);
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [manuallyAddedGroups, setManuallyAddedGroups] = useState<string[]>([]);
  const [shouldRefetchGroups, setShouldRefetchGroups] = useState(false);
  const isAlreadyInSelectedGroup = useMemo(() => {
    return runs.length &&
      selectedGroup &&
      runs.every(r => r.group === selectedGroup)
      ? true
      : false;
  }, [selectedGroup, runs]);
  const [moveRuns, moveRunState] = useRunMover({
    groupName: selectedGroup,
    entityName,
    projectName,
  });

  const onMoveRuns = async () => {
    await moveRuns(runs);
    if (shouldRefetchGroups) {
      await refetchRunGroups();
      setShouldRefetchGroups(false);
    }
    onMovedRuns();
  };

  const onCancel = () => {
    if (moveRunState.status === 'running') {
      // Clicking cancel while the move is in progress cancels outstanding runs
      // if the user hits ok on the confirm dialog.
      const response = confirm(
        `Clicking 'Ok' will stop moving remaining runs to ${selectedGroup}.`
      );
      if (response) {
        moveRunState.cancel();
      }
    } else {
      onClose();
    }
  };

  const title = `Move ${maybePluralizeWord(
    runs.length,
    'run'
  )} to another group`;

  const label =
    runs.length === 1
      ? `Move ${runs[0].displayName} to:`
      : `Move ${runs.length} runs to:`;

  const isMovingRuns = moveRunState.status === 'running';
  const onAddItem = useCallback(
    item => {
      setSelectedGroup(item);
      setManuallyAddedGroups(prev => {
        if (prev.includes(item)) {
          return prev;
        }
        return [item, ...prev];
      });
      setShouldRefetchGroups(true);
    },
    [setSelectedGroup, setManuallyAddedGroups, setShouldRefetchGroups]
  );

  return (
    <Container isInReport={isInReport}>
      <Header onClose={isMovingRuns ? null : onClose} title={title} />
      <Body>
        {loading ? (
          <InstrumentedCenteredWaveLoader name="load-all-groups" size="small" />
        ) : (
          <InlineCombobox
            disabled={isMovingRuns}
            emptyLabel="No groups match this search"
            emptyDescription="Try another search term or explore available groups."
            // If the user has created a new group, the selected group will not appear in
            // the groups provided by the server, _unless_ they accidentally typed in an
            // existing group name. If they have typed in a new unique name, stick it at
            // the front of the list.
            items={[...manuallyAddedGroups, ...groups]}
            itemToString={v => v || ''}
            label={label}
            newItemButtonLabel="New group"
            newItemPlaceholder="Type a group name..."
            onAddItem={onAddItem}
            onSelectedItemChange={setSelectedGroup}
            placeholder="Search groups"
            selectedItem={selectedGroup || null}
          />
        )}
      </Body>
      {isMovingRuns && moveRunState.total > 10 ? (
        <Footer>
          <p className="grow">
            Moving run {moveRunState.progress} of {moveRunState.total}
          </p>
          <p>
            {Math.round((moveRunState.progress / moveRunState.total) * 100)}%
          </p>
          <Progress value={moveRunState.progress} max={moveRunState.total} />
        </Footer>
      ) : null}
      <Footer>
        <Button
          variant="secondary"
          data-test="cancel-move-runs"
          className="w-full"
          onClick={onCancel}>
          Cancel
        </Button>
        <Button
          icon={isMovingRuns ? 'loading' : undefined}
          data-test="move-runs"
          disabled={isMovingRuns || !selectedGroup || isAlreadyInSelectedGroup}
          className="w-full"
          onClick={() => onMoveRuns()}>
          {isMovingRuns ? 'Moving...' : 'Move'}
        </Button>
      </Footer>
    </Container>
  );
}
